import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "../../lib/api";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { StarDisplay } from "../../components/StarRating";

const FILTERS = [
    { key: "all", label: "Todas" },
    { key: "featured", label: "Destacadas" },
    { key: "hidden", label: "Ocultas" },
];

export default function AdminReviews() {
    const [reviews, setReviews] = useState([]);
    const [filter, setFilter] = useState("all");
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/reviews");
            setReviews(data || []);
        } catch (err) {
            toast.error(apiErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const patch = async (r, changes) => {
        try {
            const { data } = await api.patch(`/admin/reviews/${r.id}`, changes);
            setReviews((list) => list.map((x) => (x.id === r.id ? { ...x, ...data.review } : x)));
            toast.success("Reseña actualizada.");
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const remove = async (r) => {
        if (!window.confirm(`¿Eliminar la reseña de ${r.author_name}? Esta acción no se puede deshacer.`)) return;
        try {
            await api.delete(`/admin/reviews/${r.id}`);
            setReviews((list) => list.filter((x) => x.id !== r.id));
            toast.success("Reseña eliminada.");
        } catch (err) { toast.error(apiErrorMessage(err)); }
    };

    const stats = useMemo(() => ({
        total: reviews.length,
        featured: reviews.filter((r) => r.featured).length,
        hidden: reviews.filter((r) => r.hidden).length,
    }), [reviews]);

    const shown = useMemo(() => {
        if (filter === "featured") return reviews.filter((r) => r.featured);
        if (filter === "hidden") return reviews.filter((r) => r.hidden);
        return reviews;
    }, [reviews, filter]);

    return (
        <DashboardLayout role="admin">
            <div className="mb-8" data-testid="admin-reviews-page">
                <div className="label-mono mb-3">/ Moderación</div>
                <h1 className="font-heading font-black text-3xl lg:text-4xl tracking-tighter">Reseñas</h1>
                <p className="text-sm text-avx-muted mt-2 max-w-2xl">
                    Aprueba, oculta o elimina reseñas de plugins. Destaca las mejores para que aparezcan primero en la
                    ficha del plugin y en los testimonios de la portada.
                </p>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6 max-w-xl">
                {[["Total", stats.total], ["Destacadas", stats.featured], ["Ocultas", stats.hidden]].map(([l, v]) => (
                    <div key={l} className="panel p-4">
                        <div className="label-mono text-[10px] mb-1">{l}</div>
                        <div className="font-heading font-black text-2xl">{v}</div>
                    </div>
                ))}
            </div>

            <div className="flex gap-2 mb-5">
                {FILTERS.map((f) => (
                    <button
                        key={f.key}
                        onClick={() => setFilter(f.key)}
                        className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] rounded-sm border transition-colors ${filter === f.key ? "bg-avx-blue text-white border-avx-blue" : "border-avx-line text-avx-muted hover:text-white"}`}
                        data-testid={`review-filter-${f.key}`}
                    >
                        {f.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="panel p-10 text-center text-avx-muted" data-testid="reviews-loading">Cargando…</div>
            ) : shown.length === 0 ? (
                <div className="panel p-10 text-center text-avx-muted" data-testid="no-reviews">No hay reseñas en esta vista.</div>
            ) : (
                <div className="space-y-3">
                    {shown.map((r) => (
                        <div
                            key={r.id}
                            className={`panel-elevated p-5 ${r.hidden ? "opacity-60" : ""}`}
                            data-testid={`admin-review-${r.id}`}
                        >
                            <div className="flex flex-wrap items-start justify-between gap-4">
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <StarDisplay value={r.rating} size={14} showCount={false} />
                                        <span className="font-heading font-bold text-sm">{r.author_name}</span>
                                        {r.verified && (
                                            <span className="text-[10px] font-mono uppercase tracking-wide text-avx-success border border-avx-success/30 rounded-sm px-1.5 py-0.5">Compra verificada</span>
                                        )}
                                        {r.featured && (
                                            <span className="text-[10px] font-mono uppercase tracking-wide text-amber-400 border border-amber-400/40 rounded-sm px-1.5 py-0.5" data-testid={`review-featured-badge-${r.id}`}>★ Destacada</span>
                                        )}
                                        {r.hidden && (
                                            <span className="text-[10px] font-mono uppercase tracking-wide text-avx-muted border border-avx-line rounded-sm px-1.5 py-0.5">Oculta</span>
                                        )}
                                    </div>
                                    <div className="label-mono text-[10px] mb-2">
                                        {r.product_name} · {(r.created_at || "").slice(0, 10)}
                                    </div>
                                    {r.title && <div className="font-heading font-bold text-sm mb-1">{r.title}</div>}
                                    {r.comment && <p className="text-sm text-avx-muted whitespace-pre-line">{r.comment}</p>}
                                </div>
                                <div className="flex flex-col gap-2 shrink-0 w-40">
                                    <button
                                        onClick={() => patch(r, { featured: !r.featured })}
                                        className={`py-2 text-xs font-mono uppercase tracking-[0.16em] rounded-sm border transition-colors ${r.featured ? "border-amber-400/40 text-amber-400 hover:bg-amber-400/10" : "btn-secondary"}`}
                                        data-testid={`toggle-featured-${r.id}`}
                                    >
                                        {r.featured ? "Quitar destacado" : "★ Destacar"}
                                    </button>
                                    <button
                                        onClick={() => patch(r, { hidden: !r.hidden })}
                                        className="btn-secondary py-2 text-xs font-mono uppercase tracking-[0.16em] rounded-sm"
                                        data-testid={`toggle-hidden-${r.id}`}
                                    >
                                        {r.hidden ? "Mostrar" : "Ocultar"}
                                    </button>
                                    <button
                                        onClick={() => remove(r)}
                                        className="py-2 text-xs font-mono uppercase tracking-[0.16em] rounded-sm border border-avx-danger/30 text-avx-danger hover:bg-avx-danger/10"
                                        data-testid={`delete-review-${r.id}`}
                                    >
                                        Eliminar
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </DashboardLayout>
    );
}
